import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Global toast store + UI for the admin dashboard.
 *
 * Toasts are pushed from three places:
 *
 * - `apiFetch` in `src/lib/api.ts` on `ApiError` (unless `silent: true`).
 * - The TanStack Query `QueryCache` / `MutationCache` `onError` hooks in
 *   `src/lib/queryClient.ts`.
 * - Feature code that wants to confirm an action (e.g. publish success).
 *
 * Each toast carries an optional `requestId` so support requests can quote
 * the upstream Worker's `x-skey-request-id` header (Requirement 25.1). The
 * Vietnamese severity label appears above the message body.
 *
 * Keeping the store and the rendering component in the same module is
 * intentional: every consumer needs at least one of `useToastStore` or
 * `<Toaster>`, so co-locating avoids a one-line re-export module.
 *
 * Validates Requirements 25.1, 25.2.
 */

export type ToastSeverity = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  /** Upstream Worker request id, surfaced for support copy/paste. */
  requestId?: string | null;
  severity: ToastSeverity;
}

export type ToastInput = Omit<Toast, 'id'> & { id?: string };

interface ToastStore {
  toasts: Toast[];
  /** Push a new toast and return its id (auto-generated if omitted). */
  addToast(toast: ToastInput): string;
  /** Remove a toast by id. No-op if the id is unknown. */
  removeToast(id: string): void;
}

let toastCounter = 0;
function genToastId(): string {
  toastCounter += 1;
  return `t-${Date.now().toString(36)}-${toastCounter}`;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = toast.id ?? genToastId();
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          id,
          title: toast.title,
          message: toast.message,
          requestId: toast.requestId ?? null,
          severity: toast.severity,
        },
      ],
    }));
    return id;
  },
  removeToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

const SEVERITY_LABELS: Record<ToastSeverity, string> = {
  info: 'Thông tin',
  success: 'Thành công',
  warning: 'Cảnh báo',
  error: 'Lỗi',
};

const SEVERITY_CLASSES: Record<ToastSeverity, string> = {
  info: 'border-slate-200 bg-white text-slate-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  error: 'border-red-200 bg-red-50 text-red-900',
};

interface ToastItemProps {
  toast: Toast;
  onClose(id: string): void;
}

/**
 * Single rendered toast. Auto-dismisses after 5 s; the close button is
 * available the whole time. The `key` on the parent `<Toaster>` list resets
 * the timer when a toast is replaced.
 */
function ToastItem({ toast, onClose }: ToastItemProps): JSX.Element {
  useEffect(() => {
    const handle = window.setTimeout(() => onClose(toast.id), 5000);
    return () => window.clearTimeout(handle);
  }, [toast.id, onClose]);

  const label = toast.title ?? SEVERITY_LABELS[toast.severity];
  return (
    <div
      role="alert"
      aria-live={toast.severity === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto w-80 rounded-md border px-4 py-3 shadow-md ${
        SEVERITY_CLASSES[toast.severity]
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
            {label}
          </p>
          <p className="mt-1 break-words text-sm leading-snug">
            {toast.message}
          </p>
          {toast.requestId ? (
            <p className="mt-1 text-xs opacity-70">
              Mã yêu cầu:{' '}
              <code className="select-all font-mono">{toast.requestId}</code>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onClose(toast.id)}
          aria-label="Đóng"
          className="text-xs opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-current"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/**
 * Fixed top-right stack of active toasts. Mount once at the root of the SPA
 * (see `src/app/App.tsx`). Pointer events are disabled on the wrapper so the
 * stack never blocks clicks on the page below; individual toast cards
 * re-enable them.
 */
export function Toaster(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  return (
    <div
      aria-label="Thông báo"
      className="pointer-events-none fixed right-4 top-4 z-50 flex max-h-screen flex-col gap-2 overflow-hidden"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  );
}
